import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import lodashFind from 'lodash/fp/find';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import removeURLSFromString from 'common/util/removeURLSFromString';

import { SR_API_ACTION_ADMIN_UPDATE_ITEM } from 'api/actionTypes';

import postWithState from 'client/actions/postWithState';

import SrTextWithLinks from 'client/components/SrTextWithLinks';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';

export const SR_HOUSEHOLD_NOTES = 'SR_HOUSEHOLD_NOTES';
export const SR_PARTNER_NOTES = 'SR_PARTNER_NOTES';
export const SR_RENTLINX_LEAD_NOTES = 'SR_RENTLINX_LEAD_NOTES';
export const SR_PARTNER_RENTLINX_LEAD_NOTES = 'SR_PARTNER_RENTLINX_LEAD_NOTES';
export const SR_LISTING_NOTES = 'SR_LISTING_NOTES';
export const SR_LISTING_JOB_ORDER_NOTES = 'SR_LISTING_JOB_ORDER_NOTES';
export const SR_JOB_ORDER_NOTES = 'SR_JOB_ORDER_NOTES';
export const SR_LISTING_NOTES_VISIBLE_TO_PARTNER = 'SR_LISTING_NOTES_VISIBLE_TO_PARTNER';

export const SR_NOTES_LIST_THEME_WHITNEY = 'notesListThemeWhitney';
export const SR_NOTES_LIST_THEME_GREYCLIFF = 'notesListThemeGreycliff';

const SR_NOTES_LIST_THEMES = [SR_NOTES_LIST_THEME_WHITNEY, SR_NOTES_LIST_THEME_GREYCLIFF];

const getTableNotes = (type) => {
  switch (type) {
    case SR_PARTNER_NOTES:
      return 'partnerNotes';
    case SR_RENTLINX_LEAD_NOTES:
    case SR_PARTNER_RENTLINX_LEAD_NOTES:
      return 'rentlinxLeadNotes';
    case SR_LISTING_NOTES:
    case SR_LISTING_NOTES_VISIBLE_TO_PARTNER:
      return 'listingNotes';
    case SR_JOB_ORDER_NOTES:
    case SR_LISTING_JOB_ORDER_NOTES:
      return 'jobOrderNotes';
    default:
      return 'householdNotes';
  }
};

class SrNotesList extends Component {
  static propTypes = {
    type: PropTypes.oneOf([
      SR_HOUSEHOLD_NOTES,
      SR_PARTNER_NOTES,
      SR_RENTLINX_LEAD_NOTES,
      SR_PARTNER_RENTLINX_LEAD_NOTES,
      SR_LISTING_NOTES,
      SR_LISTING_NOTES_VISIBLE_TO_PARTNER,
    ]).isRequired,
    theme: PropTypes.oneOf(SR_NOTES_LIST_THEMES),
    notes: PropTypes.array.isRequired,
    dispatch: PropTypes.func.isRequired,
    reloadDataTable: PropTypes.func.isRequired,
    currentUser: PropTypes.object.isRequired,
    openedInModal: PropTypes.bool,
    onClickShareNote: PropTypes.func,
    onDeleteNote: PropTypes.func,
  };

  static defaultProps = {
    theme: SR_NOTES_LIST_THEME_WHITNEY,
  };

  _onDeleteNote = (e) => {
    const { dispatch, reloadDataTable, type, currentUser, onDeleteNote } = this.props;
    const noteId = parseInt(e.target.dataset.noteId, 10);

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: getTableNotes(type),
        where: {
          id: noteId,
        },
        updates: {
          deletedByUserId: currentUser.id,
          deletedOnDate: new Date().toISOString(),
        },
      },
    }).then(() => {
      if (onDeleteNote) {
        this.props.onDeleteNote();
      }
      reloadDataTable(noteId);
    });
  };

  _onToggleShareNote = (e) => {
    const { dispatch, reloadDataTable, notes, type, currentUser, onClickShareNote } = this.props;

    const noteId = parseInt(e.target.dataset.noteId, 10);
    const note = lodashFind({ id: noteId }, notes);

    if (onClickShareNote) {
      const promise = onClickShareNote(note);
      if (promise?.then) {
        promise.then(() => {
          reloadDataTable(noteId);
        });
      }
    }

    if (type !== SR_LISTING_NOTES) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: getTableNotes(type),
        where: {
          id: noteId,
        },
        updates: {
          visibleToPartner: !note.visibleToPartner,
          sharedByUserId: note.visibleToPartner ? null : currentUser.id,
          sharedOnDate: note.visibleToPartner ? null : new Date().toISOString(),
        },
      },
    }).then(() => {
      reloadDataTable(noteId);
    });
  };

  _getNoteDescription = ({ note }) => {
    const { type } = this.props;

    const createdDate = momentWithAustinTimezone(note.createdDate).format(
      'MMMM D, YYYY [at] h:mm A',
    );

    switch (type) {
      case SR_NOTES_LIST_THEME_WHITNEY:
        return `${createdDate} by ${note.authorUserName}`;
      case SR_LISTING_NOTES_VISIBLE_TO_PARTNER:
      case SR_PARTNER_RENTLINX_LEAD_NOTES:
        return createdDate;
      case SR_LISTING_NOTES:
      case SR_JOB_ORDER_NOTES:
      case SR_RENTLINX_LEAD_NOTES:
        return `by ${note.authorUserName}`;
      default:
        return `${note.authorUserName} | ${createdDate}`;
    }
  };

  _getNoteHeader = ({ note }) => {
    const { type } = this.props;

    const createdDate = momentWithAustinTimezone(note.createdDate).format(
      'MMMM D, YYYY [at] h:mm A',
    );

    switch (type) {
      case SR_LISTING_NOTES:
      case SR_HOUSEHOLD_NOTES:
      case SR_JOB_ORDER_NOTES:
      case SR_RENTLINX_LEAD_NOTES:
        return `${createdDate}`;
      case SR_PARTNER_RENTLINX_LEAD_NOTES:
        return `Lead: ${note.rentlinxLead?.name}`;
      case SR_LISTING_JOB_ORDER_NOTES:
        return `Job Order #${note.jobOrderId}`;
      default:
        return null;
    }
  };

  _shouldHighlightNote = ({ note }) => {
    const { type } = this.props;
    switch (type) {
      case SR_LISTING_NOTES:
        return note.visibleToPartner;
      case SR_HOUSEHOLD_NOTES:
        return note.householdSharedListingNotes?.length > 0;
      case SR_RENTLINX_LEAD_NOTES:
        return note.rentlinxLeadSharedListingNotes?.length > 0;
      default:
        return false;
    }
  };

  _shouldShowShareNoteOptions = () => {
    const { type, onClickShareNote } = this.props;
    switch (type) {
      case SR_LISTING_NOTES:
        return true;
      case SR_HOUSEHOLD_NOTES:
      case SR_RENTLINX_LEAD_NOTES:
        return !!onClickShareNote;
      default:
        return false;
    }
  };

  _shouldDeleteDisplayNoteOption = () => {
    const { type } = this.props;
    switch (type) {
      case SR_LISTING_NOTES_VISIBLE_TO_PARTNER:
      case SR_PARTNER_RENTLINX_LEAD_NOTES:
      case SR_LISTING_JOB_ORDER_NOTES:
        return false;
      default:
        return true;
    }
  };

  render() {
    const { notes, openedInModal, theme, type, maxHeight } = this.props;

    const filteredNotes = notes.filter(({ deletedOnDate }) => !deletedOnDate);
    const shouldShowShareNoteOptions = this._shouldShowShareNoteOptions();
    const shouldDeleteDisplayNoteOption = this._shouldDeleteDisplayNoteOption();

    return (
      <div className={classnames('SrNotesList', theme)}>
        <div
          className={classnames({
            list: true,
            openedInModal: openedInModal,
          })}
          style={{ maxHeight }}
        >
          {type === SR_LISTING_JOB_ORDER_NOTES && filteredNotes.length === 0 && (
            <SrBox>
              <SrText font="greycliff" fontSize="16px" weight="600" color="grey">
                Notes from all orders for this listing will be displayed here.
              </SrText>
            </SrBox>
          )}
          {filteredNotes.map((note) => {
            const header = this._getNoteHeader({ note });
            const description = this._getNoteDescription({ note });
            const highlighted = this._shouldHighlightNote({ note });

            return (
              <div
                key={note.id}
                className={classnames('noteRow', {
                  highlighted,
                })}
              >
                <div className="header">
                  <span>{header}</span>
                  {shouldShowShareNoteOptions && (
                    <span
                      className="action"
                      data-note-id={note.id}
                      onClick={this._onToggleShareNote}
                    >
                      <span> - </span>
                      {highlighted ? 'Unshare Note to Partner' : 'Share Note to Partner'}
                    </span>
                  )}
                </div>
                <span className="note">
                  {type === SR_LISTING_NOTES_VISIBLE_TO_PARTNER && note.visibleToPartner ? (
                    <SrText>{removeURLSFromString(note.note)} </SrText>
                  ) : (
                    <SrTextWithLinks text={note.note} />
                  )}
                </span>
                <div className="description">
                  <span>{description}</span>
                  {shouldDeleteDisplayNoteOption && (
                    <span className="action" data-note-id={note.id} onClick={this._onDeleteNote}>
                      <span> - </span>
                      Delete
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {notes.length > 0 && <div className="line" />}
      </div>
    );
  }
}

SrNotesList.displayName = 'SrNotesList';

export default SrNotesList;
